import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Spinner,
  ListGroup,
  InputGroup,
  FormControl,
  Badge
} from 'react-bootstrap';
import { getAllItemsByLocalId } from '../../../../../../../../crud/menu_api/items';
import { AddItemToSelectionGroup } from '../../../../../../../../crud/menu_api/selection_groups';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../../../../../../../utils/functions';
import './AddItemToGroupModal.scss';
import { X } from 'react-bootstrap-icons';
import { FaSearch, FaCheck, FaPlus } from 'react-icons/fa';
import FilterDropdown from 'views/Restaurant/Menu/Menu/components/Items/components/Table/components/FilterDropdown/FilterDropdown';


interface SelectionGroupType {
  readonly selection_group_id: number;
  selection_group_name: string;
  selection_group_description?: string;
  local_id: number;
  items_affected?: { item_id: number; item_name: string; }[];
  items_contained?: { item_id: number; item_name: string; }[];
}

interface AddItemToGroupModalProps {
  show: boolean;
  handleClose: () => void;
  sg: SelectionGroupType;
  groupItems: any[];
  setGroupItems: (items: any[]) => void;
  setAddingItems?: (loading: boolean) => void;
  onItemsAdded?: (newItems: any[]) => void; // Nueva prop para notificar al padre
}

const AddItemToGroupModal: React.FC<AddItemToGroupModalProps> = ({
  show,
  handleClose,
  sg,
  groupItems,
  setGroupItems,
  setAddingItems,
  onItemsAdded,
}) => {
  const [allItems, setAllItems] = useState<any[]>([]); // fetched raw
  const [availableItems, setAvailableItems] = useState<any[]>([]); // excludes already in group
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState<any[]>([]); // Changed to support multiple items
  const [showItemSelector, setShowItemSelector] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState<boolean>(true);

  // Categories for filtering
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);

  // Fetch raw items only when modal opens
  useEffect(() => {
    if (!show) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user ? user.local_id : 0;
    setLoadingItems(true);
    getAllItemsByLocalId(localId)
      .then(r => {
        setAllItems(r.data || []);
        // Extract unique categories from items
        const categories = new Map();
        (r.data || []).forEach((item: any) => {
          if (item.category_id && item.category_name) {
            categories.set(item.category_id, {
              category_id: item.category_id,
              category_name: item.category_name
            });
          }
        });
        setAllCategories(Array.from(categories.values()));
      })
      .catch(err => console.error('Error fetching items', err))
      .finally(()=> setLoadingItems(false));
  }, [show]);

  // Derive available items (exclude existing)
  useEffect(() => {
    const existingIds = new Set((groupItems || []).map(g => g.item_id));
    setAvailableItems(allItems.filter((it: any) => !existingIds.has(it.item_id)));
  }, [allItems, groupItems]);

  // Filter items based on search query
  useEffect(() => {
    let base = availableItems || [];
    
    // Filter by categories first
    if (selectedCategories.length > 0) {
      const selectedCategoryIds = selectedCategories.map(cat => cat.value);
      base = base.filter((item: any) => selectedCategoryIds.includes(item.category_id));
    }
    
    // Then filter by search query
    const q = normalize(searchQuery);
    if (!q) { 
      setFilteredItems(base); 
      return; 
    }
    
    const tokens = q.split(' ').filter(Boolean);
    const filtered = base.filter((item: any) => {
      const haystack = normalize(item?.item_name + ' ' + item?.item_label);
      return tokens.every(tok => haystack.includes(tok));
    });
    setFilteredItems(filtered);
  }, [searchQuery, availableItems, selectedCategories]);

  // Normalization helper (remove accents, spaces, convert to lowercase)
  const normalize = (str: string) =>
    (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/\s+/g, ' ')
      .trim();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleItemSelect = (item: any) => {
    const isAlreadySelected = selectedItems.some(selected => selected.item_id === item.item_id);
    
    if (isAlreadySelected) {
      setSelectedItems(selectedItems.filter(selected => selected.item_id !== item.item_id));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleRemoveSelectedItem = (itemId: number) => {
    setSelectedItems(selectedItems.filter(item => item.item_id !== itemId));
  };

  const isItemSelected = (itemId: number) => {
    return selectedItems.some(item => item.item_id === itemId);
  };

  const handleConfirmAdd = () => {
    if (selectedItems.length === 0) {
      toast.error('No se ha seleccionado ningún ítem');
      return;
    }

    setConfirmLoading(true);
    setAddingItems?.(true);

    // Create a for loop to add all selected items with default values
    const addPromises = selectedItems.map(item => {
      const payload = {
        selection_group_id: sg.selection_group_id,
        item_id: item.item_id,
        desk_price_modifier: item.price_desk || 0,
        delivery_price_modifier: item.price_delivery || 0,
        online_price_modifier: item.price_online || 0,
        price_modifier: item.price || 0,
        min_selections: 0, // default minimum 0
        max_selections: 1, // default maximum 1
      };

      return AddItemToSelectionGroup(payload);
    });

    Promise.all(addPromises)
      .then((responses) => {
        toast.success(`${selectedItems.length} ítem${selectedItems.length > 1 ? 's' : ''} agregado${selectedItems.length > 1 ? 's' : ''} exitosamente al grupo`);
        
        // Add all new items to the group items state
        const newItems = responses.map(response => (response as any).data);
        setGroupItems([...groupItems, ...newItems]);
        
        // Notify parent component about the new items
        if (onItemsAdded) {
          onItemsAdded(newItems);
        }
        
        // Wait a bit before hiding loading to ensure the table receives the new items
        setTimeout(() => {
          setAddingItems?.(false);
          handleModalClose();
        }, 500);
      })
      .catch((error) => {
        console.error('Error agregando ítems al grupo', error);
        const message = error.response?.data?.message || 'Error al agregar los ítems al grupo';
        toast.error(message);
        setAddingItems?.(false);
      })
      .finally(() => {
        setConfirmLoading(false);
      });
  };

  const handleAddAllFromCategory = () => {
    if (selectedCategories.length === 0) return;
    
    const selectedCategoryIds = selectedCategories.map(cat => cat.value);
    const categoryItems = availableItems.filter((item: any) => 
      selectedCategoryIds.includes(item.category_id)
    );
    
    if (categoryItems.length === 0) {
      toast.info('No hay ítems disponibles en las categorías seleccionadas');
      return;
    }

    setConfirmLoading(true);
    setAddingItems?.(true);

    // Create promises for all category items
    const addPromises = categoryItems.map(item => {
      const payload = {
        selection_group_id: sg.selection_group_id,
        item_id: item.item_id,
        desk_price_modifier: item.price_desk || 0,
        delivery_price_modifier: item.price_delivery || 0,
        online_price_modifier: item.price_online || 0,
        price_modifier: item.price || 0,
        min_selections: 0,
        max_selections: 1,
      };

      return AddItemToSelectionGroup(payload);
    });

    Promise.all(addPromises)
      .then((responses) => {
        const categoryNames = selectedCategories.map(cat => cat.label).join(', ');
        const message = selectedCategories.length === 1 
          ? `${categoryItems.length} ítems de la categoría "${categoryNames}" agregados al grupo`
          : `${categoryItems.length} ítems de las categorías "${categoryNames}" agregados al grupo`;
        toast.success(message);
        
        // Add all new items to the group items state
        const newItems = responses.map(response => (response as any).data);
        setGroupItems([...groupItems, ...newItems]);
        
        // Notify parent component about the new items
        if (onItemsAdded) {
          onItemsAdded(newItems);
        }
        
        // Wait a bit before hiding loading to ensure the table receives the new items
        setTimeout(() => {
          setAddingItems?.(false);
          handleModalClose();
        }, 500);
      })
      .catch((error) => {
        console.error('Error agregando ítems de categorías al grupo', error);
        const message = error.response?.data?.message || 'Error al agregar los ítems al grupo';
        toast.error(message);
        setAddingItems?.(false);
      })
      .finally(() => {
        setConfirmLoading(false);
      });
  };

  const handleModalClose = () => {
    setSelectedItems([]);
    setSearchQuery('');
    setSelectedCategories([]);
    setShowItemSelector(false);
    handleClose();
  };

  // Highlight matched tokens in text
  const highlightMatches = (text: string) => {
    if (!searchQuery.trim()) return text;
    const tokens = normalize(searchQuery).split(' ').filter(Boolean);
    if (!tokens?.length) return text;
    const norm = (s:string)=>normalize(s);
    let remaining = text;
    const out: React.ReactNode[] = [];
    while (remaining?.length) {
      let earliest = -1; let token = '';
      for (const t of tokens) {
        const idx = norm(remaining).indexOf(t);
        if (idx !== -1 && (earliest === -1 || idx < earliest)) { earliest = idx; token = t; }
      }
      if (earliest === -1) { out.push(remaining); break; }
      if (earliest > 0) out.push(remaining.slice(0, earliest));
      const matchPortion = remaining.slice(earliest, earliest + token?.length);
      out.push(<mark key={out.length}>{matchPortion}</mark>);
      remaining = remaining.slice(earliest + token?.length);
    }
    return <>{out}</>;
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="lg" className="aig-modal">
      <Modal.Header>
        <div className='form-modal-title-container'>
          <Modal.Title className='form-modal-title'>Agregar ítems a {sg.selection_group_name}</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleModalClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body className="aig-body">
        {/* Multi-Selection Interface */}
        {showItemSelector && (
          <div className="aig-item-selector">
            {/* Search Input */}
            <div className="aig-search-container">
                <div className="aig-search-row">
                  <div className="aig-search-wrapper">
                    <InputGroup className="aig-search-input">
                      <InputGroup.Text className="aig-search-icon">
                        <FaSearch size={10} />
                      </InputGroup.Text>
                      <FormControl
                        type="text"
                        placeholder="Busca por nombre del item..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        autoFocus
                        className="aig-search-field"
                        autoComplete="off"
                      />
                    </InputGroup>
                  </div>
                  
                  {/* Category Filter Dropdown */}
                  <div className="aig-category-filter-wrapper">
                    <FilterDropdown
                      options={allCategories.map(cat => ({
                        value: cat.category_id,
                        label: cat.category_name
                      }))}
                      selectedItems={selectedCategories}
                      onChange={setSelectedCategories}
                      placeholder="Filtrar por categorías"
                    />
                  </div>
                </div>
                
                {/* Add All from Category Button */}
                {selectedCategories.length > 0 && (
                  <div className="aig-add-all-button-row">
                    <Button
                      variant="none"
                      size="sm"
                      onClick={handleAddAllFromCategory}
                      className="aig-add-all-category-btn"
                      type="button"
                      disabled={confirmLoading}
                    >
                      <FaPlus className="me-1" />
                      Agregar todos los ítems de {selectedCategories.length === 1 ? 'la categoría' : 'las categorías'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Selected Items Badges */}
              {selectedItems.length > 0 && (
                <div className="aig-selected-items-section">
                  <div className="aig-selected-items-badges">
                    {selectedItems.map((item) => (
                      <Badge 
                        key={item.item_id} 
                        className="aig-selected-item-badge"
                        bg="secondary"
                      >
                        <span className="aig-badge-text">{item.item_name}</span>
                        <Button
                          variant="link"
                          size="sm"
                          className="aig-badge-remove-btn"
                          onClick={() => handleRemoveSelectedItem(item.item_id)}
                          type="button"
                        >
                          <X size={12} />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Search Results */}
              <div className="aig-search-results">
                {loadingItems ? (
                  <div className="aig-loading-state">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Buscando...
                  </div>
                ) : filteredItems.length > 0 ? (
                  <>
                    {searchQuery && (
                      <div className="aig-results-count">
                        {filteredItems.length} resultado{filteredItems.length !== 1 ? 's' : ''}
                      </div>
                    )}
                    
                    <ListGroup className="aig-items-list">
                      {filteredItems.map((item) => (
                        <ListGroup.Item
                          key={item.item_id}
                          action
                          onClick={() => handleItemSelect(item)}
                          className={`aig-item-option ${isItemSelected(item.item_id) ? 'aig-selected' : ''}`}
                        >
                          <div className="aig-item-content">
                            <div className="aig-item-main-info">
                              <div className="aig-item-name-container">
                                <span className="aig-item-name">{highlightMatches(item.item_name)}</span>
                                {item.label && item.label !== item.item_name && (
                                  <span className="aig-item-label"> • {highlightMatches(item.label)}</span>
                                )}
                              </div>
                            </div>
                            <div className="aig-item-price">
                              {item.price > 0 ? formatCurrency(item.price) : (
                                <span className="aig-free-price">Gratis</span>
                              )}
                            </div>
                            {isItemSelected(item.item_id) && (
                              <div className="aig-selected-indicator">
                                <FaCheck />
                              </div>
                            )}
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </>
                ) : searchQuery ? (
                  <div className="aig-no-results">
                    Sin resultados para "{searchQuery}"
                  </div>
                ) : (
                  <div className="aig-empty-search">
                    Escribe para buscar items...
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="aig-selector-actions">
                <Button
                  variant="secondary"
                  onClick={handleModalClose}
                  className="aig-cancel-btn"
                  type="button"
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmAdd}
                  className="aig-confirm-btn"
                  type="button"
                  disabled={selectedItems.length === 0 || confirmLoading}
                >
                  {confirmLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Agregando...
                    </>
                  ) : (
                    <>
                      <FaCheck className="me-1" />
                      Agregar {selectedItems.length > 0 ? `(${selectedItems.length})` : ''}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!showItemSelector && availableItems.length === 0 && (
            <div className="aig-empty-items-state">
              <span className="aig-empty-text">No hay ítems disponibles para agregar.</span>
            </div>
          )}
      </Modal.Body>
    </Modal>
  );
};

export default AddItemToGroupModal;
